/**
 * analyzeModel.ts
 *
 * Pure model for Analyze mode (DD and BA): turns the stored results of the two
 * runs into one list of Findings, works out which loans are held back from the
 * guideline review, links findings to the field that produced them, and builds
 * the summary numbers shown on the stat cards.
 *
 * No React and no network.
 *
 * Run 1 (completeness + consistency) comes from
 *   POST /guidelines/document_verification and /guidelines/consistency_verification
 * Run 2 (guideline review) comes from POST /guidelines/review.
 *
 * Backend shape (see design.md, Backend gaps): completeness and consistency
 * results are keyed by document name / check name for the whole job, not by
 * loan. This module attributes them to loans on the client:
 *   - A failed completeness check is attributed to the loans whose documents
 *     do not include that document type. If every loan has it, the failure
 *     cannot be tied to a loan and stays a whole-job finding that holds back
 *     every loan.
 *   - Consistency checks are whole-job findings, except in a job with exactly
 *     one loan (every BA job), where they belong to that loan.
 *
 * Requirements: 9.2, 9.3, 9.4, 9.6.
 */

import type { ExtractField } from '../extract/extractModel';

// ── Types ───────────────────────────────────────────────────────────────────

export type FindingKind = 'completeness' | 'consistency' | 'guideline';

/** Display status of a Finding, from least to most severe is PASS → FAIL. */
export type FindingStatus = 'PASS' | 'WARNING' | 'MISSING_DATA' | 'ERROR' | 'FAIL';

export interface FieldRef {
  loanId: string;
  documentId: string;
  /** `ExtractField.id`, the value used for the `field` URL param. */
  fieldId: string;
}

export interface Finding {
  id: string;
  kind: FindingKind;
  status: FindingStatus;
  /** `null` is a whole-job finding that cannot be tied to one loan. */
  loanId: string | null;
  title: string;
  detail: string;
  criteria?: string[];
  /** Set when the finding comes from a field (Requirement 9.4). */
  fieldRef?: FieldRef;
  /** Set when the field behind the finding changed after the run (9.5). */
  stale?: boolean;
}

export interface LoanInfo {
  loanId: string;
  /** Display name, for example the loan number. */
  label: string;
}

/** Stored result of a completeness or consistency run: check name → outcome. */
export type CheckResults = Record<string, { status: boolean; reason?: string }>;

/** One loan's result from the guideline review, in the shape the app uses. */
export interface GuidelineFindingInput {
  loanId: string;
  status: 'PASS' | 'FAIL' | 'WARNING' | 'FAILED' | 'MISSING DATA' | string;
  explanation: string;
  unmetCriteria?: string[] | null;
}

export interface HeldBackLoan {
  loanId: string;
  label: string;
  /** Plain-language reasons, for example "Missing: W-2 Form". */
  reasons: string[];
}

export interface AnalyzeInputs {
  loans: ReadonlyArray<LoanInfo>;
  /** Document types found per loan id. */
  docTypes: Readonly<Record<string, ReadonlyArray<string>>>;
  completeness: CheckResults | null;
  consistency: CheckResults | null;
  guideline: ReadonlyArray<GuidelineFindingInput> | null;
  /** Loans the guideline run was limited to; `null` means every loan. */
  guidelineScope: ReadonlyArray<string> | null;
}

export interface AnalyzeResult {
  loans: LoanInfo[];
  findings: Finding[];
  heldBack: HeldBackLoan[];
  ran: { completeness: boolean; consistency: boolean; guideline: boolean };
}

export interface AnalyzeSummary {
  loanCount: number;
  ran: AnalyzeResult['ran'];
  /** Required documents: loans with no completeness failure. */
  completeLoans: number;
  /** Consistency: findings that are not a pass. */
  openConsistency: number;
  /** Loans that the guideline review may cover. */
  eligibleLoans: number;
  heldBackLoans: number;
  /** Guideline review: loans that have a result inside the run scope. */
  reviewedLoans: number;
  passedLoans: number;
  /** Findings by status over the same list the table shows. */
  statusCounts: Record<FindingStatus, number>;
  total: number;
  stale: number;
}

// ── Labels ──────────────────────────────────────────────────────────────────

export const FINDING_STATUS_ORDER: ReadonlyArray<FindingStatus> = [
  'FAIL',
  'ERROR',
  'MISSING_DATA',
  'WARNING',
  'PASS',
];

export const FINDING_STATUS_LABELS: Readonly<Record<FindingStatus, string>> = {
  FAIL: 'Fail',
  ERROR: 'Review error',
  MISSING_DATA: 'Missing data',
  WARNING: 'Warning',
  PASS: 'Pass',
};

export const FINDING_KIND_LABELS: Readonly<Record<FindingKind, string>> = {
  completeness: 'Required documents',
  consistency: 'Consistency',
  guideline: 'Guideline review',
};

/** Map the guideline review's status strings onto display statuses. */
export function mapGuidelineStatus(raw: string): FindingStatus {
  switch (raw.toUpperCase().replace(/_/g, ' ')) {
    case 'PASS':
      return 'PASS';
    case 'FAIL':
      return 'FAIL';
    case 'WARNING':
      return 'WARNING';
    case 'MISSING DATA':
      return 'MISSING_DATA';
    default:
      // 'FAILED' is the agent failing to review the loan, not the loan failing.
      return 'ERROR';
  }
}

/** "income_matches_across_documents" → "Income matches across documents". */
export function humanizeCheckName(name: string): string {
  const text = name.replace(/[_]+/g, ' ').replace(/\s+/g, ' ').trim();
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : name;
}

function normalize(text: string): string {
  return text.toLowerCase().replace(/[^a-z0-9]/g, '');
}

/** True when a stored document type satisfies a required-document check name. */
export function docTypeMatchesCheck(docType: string, checkName: string): boolean {
  const d = normalize(docType);
  const c = normalize(checkName);
  if (!d || !c) return false;
  return d === c || (c.length >= 4 && d.includes(c)) || (d.length >= 4 && c.includes(d));
}

// ── Building findings ───────────────────────────────────────────────────────

interface CompletenessFailure {
  check: string;
  reason: string;
  /** True when specific loans lack the document; false when it is whole-job. */
  loanSpecific: boolean;
  loanIds: string[];
}

function completenessFailures(inputs: AnalyzeInputs): CompletenessFailure[] {
  if (!inputs.completeness) return [];
  const failures: CompletenessFailure[] = [];
  for (const [check, outcome] of Object.entries(inputs.completeness)) {
    if (outcome.status) continue;
    const lacking = inputs.loans
      .filter((loan) => !(inputs.docTypes[loan.loanId] ?? []).some((t) => docTypeMatchesCheck(t, check)))
      .map((loan) => loan.loanId);
    const loanSpecific = lacking.length > 0;
    failures.push({
      check,
      reason:
        outcome.reason?.trim() ||
        (loanSpecific
          ? `${humanizeCheckName(check)} was not found.`
          : `${humanizeCheckName(check)} did not meet the guideline's requirement.`),
      loanSpecific,
      loanIds: lacking,
    });
  }
  return failures;
}

function sortFindings(findings: Finding[], loans: ReadonlyArray<LoanInfo>): Finding[] {
  const kindOrder: FindingKind[] = ['completeness', 'consistency', 'guideline'];
  const labelOf = new Map(loans.map((l) => [l.loanId, l.label]));
  return [...findings].sort((a, b) => {
    const s = FINDING_STATUS_ORDER.indexOf(a.status) - FINDING_STATUS_ORDER.indexOf(b.status);
    if (s !== 0) return s;
    const k = kindOrder.indexOf(a.kind) - kindOrder.indexOf(b.kind);
    if (k !== 0) return k;
    const la = a.loanId ? (labelOf.get(a.loanId) ?? a.loanId) : '';
    const lb = b.loanId ? (labelOf.get(b.loanId) ?? b.loanId) : '';
    const l = la.localeCompare(lb);
    return l !== 0 ? l : a.id.localeCompare(b.id);
  });
}

/**
 * Build the findings and the held-back loans from the stored results.
 *
 * A loan is held back from the guideline review while any completeness check
 * fails for it. Passing completeness findings are produced per complete loan so
 * the single-loan view can say "complete" as well as "missing".
 */
export function buildAnalyze(inputs: AnalyzeInputs): AnalyzeResult {
  const { loans } = inputs;
  const soleLoanId = loans.length === 1 ? loans[0].loanId : null;
  const findings: Finding[] = [];

  // Completeness
  // A whole-job failure (no loan lacks the document) holds every loan back.
  const failures = completenessFailures(inputs);
  const reasonsByLoan = new Map<string, string[]>();
  const addReason = (loanId: string, reason: string) => {
    const list = reasonsByLoan.get(loanId) ?? [];
    if (!list.includes(reason)) list.push(reason);
    reasonsByLoan.set(loanId, list);
  };

  for (const failure of failures) {
    const title = humanizeCheckName(failure.check);
    if (failure.loanSpecific) {
      for (const loanId of failure.loanIds) {
        addReason(loanId, `Missing: ${title}`);
        findings.push({
          id: `completeness:${failure.check}:${loanId}`,
          kind: 'completeness',
          status: 'FAIL',
          loanId,
          title,
          detail: failure.reason,
        });
      }
    } else {
      for (const loan of loans) addReason(loan.loanId, `Not met: ${title}`);
      findings.push({
        id: `completeness:${failure.check}`,
        kind: 'completeness',
        status: 'FAIL',
        loanId: soleLoanId,
        title,
        detail: failure.reason,
      });
    }
  }

  const heldBack: HeldBackLoan[] = loans
    .filter((loan) => reasonsByLoan.has(loan.loanId))
    .map((loan) => ({
      loanId: loan.loanId,
      label: loan.label,
      reasons: reasonsByLoan.get(loan.loanId) ?? [],
    }));

  if (inputs.completeness) {
    for (const loan of loans) {
      if (reasonsByLoan.has(loan.loanId)) continue;
      findings.push({
        id: `completeness:pass:${loan.loanId}`,
        kind: 'completeness',
        status: 'PASS',
        loanId: loan.loanId,
        title: 'Required documents',
        detail: 'All required documents are present.',
      });
    }
  }

  // Consistency: whole-job checks, attributed to the loan in a one-loan job.
  if (inputs.consistency) {
    for (const [check, outcome] of Object.entries(inputs.consistency)) {
      if (outcome.status) continue;
      findings.push({
        id: `consistency:${check}`,
        kind: 'consistency',
        status: 'WARNING',
        loanId: soleLoanId,
        title: humanizeCheckName(check),
        detail: outcome.reason?.trim() || 'The documents disagree on this check.',
      });
    }
  }

  // Guideline review, limited to the run's scope.
  if (inputs.guideline) {
    const scope = inputs.guidelineScope ? new Set(inputs.guidelineScope) : null;
    const known = new Set(loans.map((l) => l.loanId));
    for (const item of inputs.guideline) {
      if (scope && !scope.has(item.loanId)) continue;
      if (!known.has(item.loanId)) continue;
      findings.push({
        id: `guideline:${item.loanId}`,
        kind: 'guideline',
        status: mapGuidelineStatus(String(item.status)),
        loanId: item.loanId,
        title: 'Guideline review',
        detail: item.explanation,
        criteria: item.unmetCriteria?.length ? [...item.unmetCriteria] : undefined,
      });
    }
  }

  return {
    loans: [...loans],
    findings: sortFindings(findings, loans),
    heldBack,
    ran: {
      completeness: inputs.completeness !== null,
      consistency: inputs.consistency !== null,
      guideline: inputs.guideline !== null,
    },
  };
}

/** Loans the guideline review may cover: all, or only those not held back. */
export function scopeLoanIds(
  result: Pick<AnalyzeResult, 'loans' | 'heldBack'>,
  scope: 'eligible' | 'all'
): string[] {
  if (scope === 'all') return result.loans.map((l) => l.loanId);
  const held = new Set(result.heldBack.map((h) => h.loanId));
  return result.loans.filter((l) => !held.has(l.loanId)).map((l) => l.loanId);
}

// ── Linking findings to fields ──────────────────────────────────────────────

/** Smallest field label the text match accepts, to avoid matching "id" or "no". */
const MIN_LABEL_LENGTH = 4;

function escapeRegExp(text: string): string {
  return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * The field a finding is about, found by looking for a field's label in the
 * finding's text ("Maturity date read with low confidence" → `maturity date`).
 * The longest matching label wins; ties go to the first field. Returns
 * undefined when nothing matches.
 *
 * The backend does not return a field reference with findings (design.md,
 * Backend gaps), so this is the client-side link until it does.
 */
export function findFieldForText(
  text: string,
  fields: ReadonlyArray<ExtractField>
): ExtractField | undefined {
  const haystack = ` ${text.toLowerCase().replace(/[_\-]+/g, ' ').replace(/\s+/g, ' ')} `;
  let best: ExtractField | undefined;
  let bestLength = 0;
  for (const field of fields) {
    const label = field.label.toLowerCase().replace(/\s+/g, ' ').trim();
    if (label.length < MIN_LABEL_LENGTH || label.length <= bestLength) continue;
    const pattern = new RegExp(`(?:^|[^a-z0-9])${escapeRegExp(label)}(?:[^a-z0-9]|$)`);
    if (pattern.test(haystack)) {
      best = field;
      bestLength = label.length;
    }
  }
  return best;
}

/** Add `fieldRef` to consistency and guideline findings whose loan has a matching field. */
export function linkFindingsToFields(
  findings: ReadonlyArray<Finding>,
  fieldsByLoan: Readonly<Record<string, ReadonlyArray<ExtractField>>>
): Finding[] {
  return findings.map((finding) => {
    if (finding.kind === 'completeness' || finding.status === 'PASS' || !finding.loanId) {
      return finding;
    }
    const fields = fieldsByLoan[finding.loanId];
    if (!fields?.length) return finding;
    const text = [finding.title, finding.detail, ...(finding.criteria ?? [])].join(' ');
    const match = findFieldForText(text, fields);
    if (!match) return finding;
    return {
      ...finding,
      fieldRef: { loanId: finding.loanId, documentId: match.documentId, fieldId: match.id },
    };
  });
}

// ── Views and counts ────────────────────────────────────────────────────────

/**
 * The findings one view shows. `null` is the job-wide view (every finding).
 * A loan view shows that loan's findings plus the whole-job findings that apply
 * to every loan. Both views read from the same list, so a finding keeps its
 * status and badge in both (Requirement 9.6).
 */
export function findingsForLoan(findings: ReadonlyArray<Finding>, loanId: string | null): Finding[] {
  if (loanId === null) return [...findings];
  return findings.filter((f) => f.loanId === loanId || f.loanId === null);
}

/** Summary numbers for the job-wide view (`loanId` null) or one loan. */
export function summarize(result: AnalyzeResult, loanId: string | null): AnalyzeSummary {
  const loans = loanId === null ? result.loans : result.loans.filter((l) => l.loanId === loanId);
  const loanIds = new Set(loans.map((l) => l.loanId));
  const held = new Set(result.heldBack.map((h) => h.loanId));
  const visible = findingsForLoan(result.findings, loanId);

  const statusCounts = Object.fromEntries(FINDING_STATUS_ORDER.map((s) => [s, 0])) as Record<
    FindingStatus,
    number
  >;
  for (const f of visible) statusCounts[f.status] += 1;

  const heldBackLoans = loans.filter((l) => held.has(l.loanId)).length;
  const guidelineByLoan = new Map<string, Finding>();
  for (const f of visible) {
    if (f.kind === 'guideline' && f.loanId && loanIds.has(f.loanId)) guidelineByLoan.set(f.loanId, f);
  }

  return {
    loanCount: loans.length,
    ran: result.ran,
    completeLoans: result.ran.completeness ? loans.length - heldBackLoans : 0,
    openConsistency: visible.filter((f) => f.kind === 'consistency' && f.status !== 'PASS').length,
    eligibleLoans: loans.length - heldBackLoans,
    heldBackLoans,
    reviewedLoans: guidelineByLoan.size,
    passedLoans: Array.from(guidelineByLoan.values()).filter((f) => f.status === 'PASS').length,
    statusCounts,
    total: visible.length,
    stale: visible.filter((f) => f.stale).length,
  };
}
